import { types as api } from '@mesa-labs/mesa-api';
import {
  MesaColumn, MesaDensity, MesaTable, StatusLabel,
} from '@mesa-labs/mesa-ui';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { formatDateTime, formatNumericMonthDay } from '../../../../utils';
import {
  actionItemsTableOrder, actionItemStaleAge, ActionItemStalenessColors, actionItemStatusColorKey, actionItemStatusLabel, compareActionItemCreated, compareActionItemOwner, compareActionItemStatus,
  compareActionItemVendor,
  formatAssignee, formatAssigneeEmail, isAssignedTo,
} from '../../../../utils/vendorActionItems';
import { AssigneeAvatarTooltip } from './Assignee';
import { compareActionItemPartner } from '../../../../utils/actionItemPartners';
import MoveToMenu from './MoveToMenu';
import StaleAge from './StaleAge';
import { Divider } from './styles';
import { PartnerChips } from './PartnerTag';

const ItemCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.span<{ $canceled: boolean }>`
  color: #101828;
  font-weight: 500;
  overflow-wrap: anywhere;
  /* The cell is nowrap so the latest Note truncates; the title wraps instead. */
  white-space: normal;
  ${(p) => p.$canceled && 'text-decoration: line-through;'}
`;

const LatestNote = styled.span`
  color: ${(p) => p.theme.colors.Gray500};
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Inline so the cell's ellipsis truncates long names.
const VendorLink = styled(Link)`
  color: #475467;
  font-weight: 500;

  &:hover { color: #6941C6; text-decoration: underline; }
`;

// The cell centers with text-align, which doesn't move the Tooltip's block-level container.
const CenteredCell = styled.div`
  display: flex;
  justify-content: center;
`;

const RowActions = styled.span`
  align-items: center;
  color: ${(p) => p.theme.colors.Gray500};
  display: inline-flex;
  font-size: 11px;
  gap: 4px;
`;

type Props = {
  items: readonly api.VendorActionItemResponse[];
  me?: string;
  selectedId?: string;
  canWrite: boolean;
  density?: MesaDensity;
  // Adds a Vendor column, for tables that span Vendors.
  showVendor?: boolean;
  // Drops the Owner column, for tables already grouped by owner.
  showOwner?: boolean;
  // Adds a sortable Partner column of partner chips.
  showPartner?: boolean;
  onOpen: (item: api.VendorActionItemResponse) => void;
  // Adds the trailing column: the Note count, a stale item's idle age, and (with write access) the status menu.
  onMove?: (item: api.VendorActionItemResponse, status: api.VendorActionItemStatus) => void;
};

const vendorColumn: MesaColumn<api.VendorActionItemResponse> = {
  key: 'vendorName',
  label: 'Vendor',
  type: 'component',
  width: 220,
  enableSorting: true,
  sortingFn: (a, b) => compareActionItemVendor(a.original, b.original),
  render: (_value, item) => (
    <VendorLink to={`/vendors/${item.vendorId}`} title={item.vendorName} onClick={(e) => e.stopPropagation()}>
      {item.vendorName}
    </VendorLink>
  ),
};

const ownerColumn = (me?: string): MesaColumn<api.VendorActionItemResponse> => ({
  key: 'assignedTo',
  label: 'Owner',
  type: 'component',
  width: 72,
  align: 'center',
  enableSorting: true,
  sortingFn: (a, b) => compareActionItemOwner(a.original, b.original),
  render: (_value, item) => (
    <CenteredCell>
      <AssigneeAvatarTooltip email={item.assignedTo} label={`${formatAssignee(item.assignedTo)}${isAssignedTo(item, me) ? ' (Me)' : ''}`} />
    </CenteredCell>
  ),
});

const partnerColumn: MesaColumn<api.VendorActionItemResponse> = {
  key: 'partner',
  label: 'Partner',
  type: 'component',
  width: 150,
  // Chips wrap onto more lines rather than clipping under the cell's ellipsis.
  wrapValue: true,
  enableSorting: true,
  sortingFn: (a, b) => compareActionItemPartner(a.original, b.original),
  render: (_value, item) => <PartnerChips item={item} />,
};

const buildColumns = (
  me?: string,
  showVendor = false,
  showOwner = true,
  showPartner = false,
): MesaColumn<api.VendorActionItemResponse>[] => [
  {
    key: 'status',
    label: 'Status',
    type: 'component',
    width: 116,
    enableSorting: true,
    sortingFn: (a, b) => compareActionItemStatus(a.original, b.original),
    render: (_value, item) => <StatusLabel status={actionItemStatusColorKey(item.status)} label={actionItemStatusLabel(item.status)} />,
  },
  ...(showOwner ? [ownerColumn(me)] : []),
  ...(showVendor ? [vendorColumn] : []),
  ...(showPartner ? [partnerColumn] : []),
  {
    key: 'title',
    label: 'Item',
    type: 'component',
    flex: true,
    minWidth: 200,
    enableSorting: false,
    render: (_value, item) => (
      <ItemCell>
        <Title $canceled={item.status === api.VendorActionItemStatus.CANCELED}>{item.title}</Title>
        {item.latestNote && (
          <LatestNote title={item.latestNote.note}>{`${formatNumericMonthDay(item.latestNote.createdAt)} · ${formatAssigneeEmail(item.latestNote.createdBy)}: ${item.latestNote.note}`}</LatestNote>
        )}
      </ItemCell>
    ),
  },
  {
    key: 'createdAt',
    label: 'Created',
    type: 'component',
    width: 170,
    enableSorting: true,
    sortingFn: (a, b) => compareActionItemCreated(a.original, b.original),
    render: (_value, item) => <span style={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.createdAt)}</span>,
  },
];

// Action Items as a table: status, (optionally) Vendor, (optionally) owner, title with the latest Note and created
// time; with `onMove`, a trailing cell shows the Note count and the status menu. Unsorted, open items come first, then
// closed, each newest created first; header sorts are client-side. Renders nothing without rows.
export default function ActionItemsTable({
  items, me, selectedId, canWrite, density = 'standard', showVendor, showOwner, showPartner, onOpen, onMove,
}: Props): React.ReactElement | null {
  const rows = useMemo(() => actionItemsTableOrder(items), [items]);
  // Once per row for both the row tint and the idle age.
  const staleAges = useMemo(() => new Map(rows.map((item) => [item.id, actionItemStaleAge(item)])), [rows]);
  // No empty state: callers say "No Vendor Action Items" in their own heading instead.
  if (rows.length === 0) return null;
  return (
    <MesaTable
      data={rows}
      columns={buildColumns(me, showVendor, showOwner, showPartner)}
      getRowId={(item) => item.id}
      density={density}
      sorting={{ mode: 'client', enabled: true }}
      minHeight={60}
      columnLayout={{ enableResizing: false }}
      onRowClick={onOpen}
      conditionalRowStyling={(item) => {
        if (item.id === selectedId) return '#F4EFFC';
        const stale = staleAges.get(item.id);
        return stale && ActionItemStalenessColors[stale.staleness].background;
      }}
      renderRowActions={onMove && ((item) => (
        <RowActions>
          {canWrite && <MoveToMenu status={item.status} onMove={(status) => onMove(item, status)} />}
          {item.noteCount > 0 && <span title={`${item.noteCount} ${item.noteCount === 1 ? 'comment' : 'comments'}`}>{`💬 ${item.noteCount}`}</span>}
          {item.noteCount > 0 && staleAges.get(item.id) && <Divider aria-hidden />}
          <StaleAge item={item} stale={staleAges.get(item.id)} />
        </RowActions>
      ))}
    />
  );
}
